import { getCustomer } from 'admin/actions/customers';
import { components } from 'admin/schema/docs/api/admin';
import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, useHistory, useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { formatUUID, toQueryParams } from '../../../utils';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import BreadCrumb from '../../molecules/BreadCrumb';
import ButtonGroup from '../../molecules/ButtonGroup';
import Balances from './Balances';
import ExpiredBalances from './ExpiredBalances';

type Customer = components['schemas']['Customer'];
type PrivateMoney = components['schemas']['PrivateMoney'];

const FormWrapper = styled.div``;

const Table = styled(Grid.Container)`
  margin-top: 12px;
`;
const GridHeader = styled(Grid.Container)``;

const TooltipGridHeaderItem = styled(Grid.Item)`
  &:hover {
    cursor: pointer;
    background-color: #eeeeee;
  }
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SelectedPrivateMoneyWrapper = styled.div`
  margin-bottom: 0.25rem;
  opacity: 0.9;
`;

const TABS: StyledComponentPropsWithRef<typeof ButtonGroup>['buttons'] = [
  { text: '有効期限', value: 'balances' },
  { text: '失効履歴', value: 'expired-balances' },
];

const crumbs = (id?: string) => [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: 'お客様管理', href: '/customers', isCurrentPage: false },
  {
    value: `お客様詳細: ${formatUUID(id) || ''}`,
    href: `/customers/${id}`,
    isCurrentPage: false,
  },
  { value: '残高詳細', href: '', isCurrentPage: true },
];

const CustomerDetailBalance = ({ match }: RouteComponentProps<{ id?: string }>) => {
  const customerId = match.params.id;
  const data = useLocation<{ selectedPrivateMoney: PrivateMoney }>();
  const history = useHistory();

  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [customer, setCustomer] = useState<Customer>({} as Customer);

  const { id: privateMoneyId, name: privateMoneyName } = data?.state?.selectedPrivateMoney || {};

  const fetchCustomer = useCallback(() => {
    if (customerId && privateMoneyId) {
      getCustomer(
        customerId,
        toQueryParams({
          private_money_id: privateMoneyId,
        })
      ).then((c) => {
        setCustomer(c);
      });
    }
  }, [customerId, data?.state?.selectedPrivateMoney.id]);

  useEffect(() => {
    if (!privateMoneyId) {
      history.replace(`/customers/${customerId}`);
      return;
    }

    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateMoneyId, fetchCustomer, history]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={crumbs(customer.id)} />
      <Headings.h5>残高詳細</Headings.h5>
      <SelectedPrivateMoneyWrapper>
        <Headings.h6>{`マネー: ${privateMoneyName}`}</Headings.h6>
      </SelectedPrivateMoneyWrapper>

      {Object.keys(customer).length !== 0 ? (
        <>
          <ButtonGroup
            buttons={TABS}
            activeIndex={selectedTabIndex}
            onClick={(_, index) => setSelectedTabIndex(index)}
          />
          <Table gridSize={1}>
            <GridHeader>
              <TooltipGridHeaderItem size={3} data-tip data-for="expiresAt">
                日付
                <ReactTooltip id="expiresAt" type="dark" place="top" effect="solid">
                  <span>
                    通常は表示されている日付の23:59:59まで有効です。時刻を指定して登録された残高は、その時刻に失効します
                  </span>
                </ReactTooltip>
              </TooltipGridHeaderItem>
              <Grid.Item size={9}>
                <span style={{ paddingLeft: '2em' }}>金額</span>
              </Grid.Item>
            </GridHeader>

            <Grid.Divider margin="0" />
            {selectedTabIndex === 0 ? (
              <Balances accountId={customer?.account_uuid} />
            ) : (
              <ExpiredBalances accountId={customer?.account_uuid} />
            )}
          </Table>
        </>
      ) : (
        <LoaderWrapper>
          <Loader type="Oval" width={50} height={50} />
        </LoaderWrapper>
      )}
    </FormWrapper>
  );
};

export default CustomerDetailBalance;
